'use client';

import type { PropsWithChildren, ReactNode } from 'react';

import type { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';

import { useReportAnswerValueSelected } from '../_atoms/draftReport/stepData';
import { useCurrentStep } from '../_atoms/draftReport/useCurrentStep';

interface PropsWithSteps extends PropsWithChildren {
	steps?: string[];
}

export function HideFromSteps({ steps, children }: PropsWithSteps): ReactNode {
	// This component is used to hide the ObservationButton from certain steps
	const currentStep = useCurrentStep();

	if (steps?.includes(currentStep)) {
		return null;
	}

	return children;
}

interface ShowIfAnswerSelectedProps extends PropsWithChildren {
	question: ReportQuestion;

	// One or more answer keys to check for selection. If at least one is selected, the content will be shown
	answerKey?: string;

	// Alternate content to show if not selected
	contentIfUnselected?: ReactNode;

	// If true, the content will always be shown even when the selected value is undefined
	showIfSelectedIsUndefined?: boolean;
}

export function ShowIfAnswerSelected({
	question,
	answerKey,
	children,
	contentIfUnselected,
	showIfSelectedIsUndefined,
}: ShowIfAnswerSelectedProps): ReactNode {
	// Check if the current question and answer are selected
	const isSelected = useReportAnswerValueSelected(question, answerKey);

	// If the value is selected, show the content
	if (isSelected) {
		return children;
	}

	// Content has either been intentionally unselected or we want to always show it any time selected isn't true
	if (isSelected === false || showIfSelectedIsUndefined) {
		return contentIfUnselected ?? null;
	}

	return null;
}

interface ShowIfAnswerUnselectedProps extends PropsWithChildren {
	question: ReportQuestion;

	// One or more answer keys to check for selection. If at least one is selected, the content will be shown
	answerKey?: string;

	// If true, the content will always be shown even when the selected value is undefined
	showIfSelectedIsUndefined?: boolean;
}

export function ShowIfAnswerUnselected({
	question,
	answerKey,
	children,
	showIfSelectedIsUndefined,
}: ShowIfAnswerUnselectedProps): ReactNode {
	// Check if the current question and answer are selected
	const isSelected = useReportAnswerValueSelected(question, answerKey);

	// Content has either been intentionally unselected or we want to always show it any time selected isn't true
	if (isSelected === false || showIfSelectedIsUndefined) {
		return children;
	}

	return null;
}
